(() => {
  if (window.CollectionStockOrder) return;
  const selector = '#items-grid[data-stock-section]';
  const pools = new Map();
  const alphabet = new Intl.Collator('ro', { sensitivity: 'base', numeric: true });
  let initial = Promise.resolve();
  const initialController = new AbortController();
  let started = false;
  const parse = text => new DOMParser().parseFromString(text, 'text/html');
  const cards = grid => Array.from(grid.children).filter(node => node.hasAttribute('data-stock-product'));
  const manual = url => Boolean(url.searchParams.get('sort_by'));

  async function read(url, signal) {
    const timeout = AbortSignal.timeout(10000);
    const response = await fetch(url, { signal: signal ? AbortSignal.any([signal, timeout]) : timeout });
    if (!response.ok) throw new Error(`Collection request failed: ${response.status}`);
    return response.text();
  }

  async function parallel(items, task) {
    const result = new Array(items.length);
    let cursor = 0;
    await Promise.all(Array.from({ length: Math.min(3, items.length) }, async () => {
      while (cursor < items.length) {
        const index = cursor++;
        result[index] = await task(items[index]);
      }
    }));
    return result;
  }

  function metadata(grid) {
    if (!grid) throw new Error('Missing collection grid');
    const data = grid.dataset;
    const result = {
      section: data.stockSection,
      page: Number(data.stockPage),
      pages: Number(data.stockPages),
      size: Number(data.stockSize),
      count: Number(data.stockCount)
    };
    if (![result.page, result.pages, result.size, result.count].every(Number.isSafeInteger)
      || result.page < 1 || result.page > result.pages || result.pages < 1
      || result.size < 1 || result.count < 1 || result.count > 25000
      || result.pages !== Math.ceil(result.count / result.size)) {
      throw new Error('Unsupported or inconsistent collection pagination');
    }
    return result;
  }

  function sourceSnapshot(grid) {
    const copy = grid.cloneNode(true);
    const originalNodes = [grid, ...grid.querySelectorAll('*')];
    const copyNodes = [copy, ...copy.querySelectorAll('*')];
    const copies = new Map(originalNodes.map((node, i) => [node, copyNodes[i]]));
    originalNodes.forEach(node => {
      if (node._x_currentIfEl) copies.get(node._x_currentIfEl)?.remove();
      if (node._x_lookup) Object.values(node._x_lookup).forEach(child => copies.get(child)?.remove());
    });
    copy.querySelectorAll('[data-zentamine-lazy-card]').forEach(card => {
      card.setAttribute('x-ignore', '');
      card.dataset.zentamineLazyCard = '';
    });
    return { querySelector: query => query === selector ? copy : copy.querySelector(query) };
  }

  async function getPool(url, seed, signal) {
    const original = seed.querySelector(selector);
    const info = metadata(original);
    const snapshot = sourceSnapshot(original);
    const inline = seed.querySelector('[data-stock-inline]');
    const base = new URL(url);
    base.searchParams.set('section_id', info.section);
    base.searchParams.delete('page');
    base.searchParams.delete('sort_by');
    base.searchParams.sort();
    const key = `${base.href}|${info.size}|${info.count}`;
    if (pools.has(key)) return pools.get(key);
    const pending = (async () => {
      async function indexPage(page) {
        const address = new URL(base);
        address.searchParams.set('section_id', 'collection-stock-index');
        address.searchParams.set('page', String(page));
        let data;
        if (info.pages === 1) {
          data = { count: info.count, page: 1, pages: 1, products: cards(original).map(node => ({
            id: node.dataset.stockProduct, available: node.dataset.stockAvailable === 'true',
            title: node.dataset.stockTitle, rank: Number(node.dataset.stockRank)
          })) };
        } else if (page === 1 && inline) {
          data = JSON.parse(inline.textContent);
        } else {
          const node = parse(await read(address, signal)).querySelector('[data-stock-index]');
          if (!node) throw new Error('Missing collection stock index');
          data = JSON.parse(node.textContent);
        }
        const expectedPages = Math.ceil(info.count / 250);
        const expectedLength = Math.min(250, info.count - (page - 1) * 250);
        if (data.count !== info.count || data.page !== page || data.pages !== expectedPages
          || !Array.isArray(data.products) || data.products.length !== expectedLength
          || data.products.some(item => typeof item.id !== 'string'
            || typeof item.available !== 'boolean' || typeof item.title !== 'string'
            || ![0, 1, 2].includes(item.rank))) {
          throw new Error('Incomplete collection stock index');
        }
        return data;
      }
      const parts = await parallel(Array.from({ length: Math.ceil(info.count / 250) }, (_, i) => i + 1), indexPage);
      const entries = parts.flatMap(part => part.products);
      if (new Set(entries.map(item => item.id)).size !== info.count) {
        throw new Error('Duplicate or missing products in collection stock index');
      }
      const products = entries.map((item, position) => ({ ...item, page: Math.floor(position / info.size) + 1 }));
      const ordered = [...products].sort((a, b) => a.rank - b.rank
        || alphabet.compare(a.title, b.title) || alphabet.compare(a.id, b.id));
      const sources = new Map();

      function validate(doc, page) {
        const grid = doc.querySelector(selector);
        const next = metadata(grid);
        const list = cards(grid);
        const expected = products.slice((page - 1) * info.size, page * info.size);
        if (next.section !== info.section || next.page !== page || next.count !== info.count
          || next.size !== info.size || list.length !== expected.length
          || list.some((node, i) => node.dataset.stockProduct !== expected[i].id
            || node.dataset.stockAvailable !== String(expected[i].available)
            || node.dataset.stockRank !== String(expected[i].rank)
            || node.dataset.stockTitle !== expected[i].title)) {
          throw new Error('Collection changed while loading');
        }
        return list;
      }
      sources.set(info.page, Promise.resolve(validate(snapshot, info.page)));
      async function source(page, sourceSignal) {
        if (!sources.has(page)) {
          const address = new URL(base);
          address.searchParams.set('page', String(page));
          sources.set(page, read(address, sourceSignal).then(text => validate(parse(text), page)).catch(error => {
            sources.delete(page);
            throw error;
          }));
        }
        return sources.get(page);
      }
      return { ordered, source, seed: (doc, page) => sources.set(page, Promise.resolve(validate(doc, page))) };
    })();
    pools.set(key, pending);
    try { return await pending; }
    catch (error) { pools.delete(key); throw error; }
  }

  async function prepare(text, address) {
    const url = new URL(address, location.href);
    if (manual(url)) return text;
    const doc = parse(text);
    const grid = doc.querySelector(selector);
    if (!grid) return text;
    const info = metadata(grid);
    const pool = await getPool(url, doc);
    pool.seed(doc, info.page);
    const selected = pool.ordered.slice((info.page - 1) * info.size, info.page * info.size);
    const sourceCards = new Map();
    await parallel([...new Set(selected.map(item => item.page))], async page => {
      (await pool.source(page)).forEach(node => sourceCards.set(node.dataset.stockProduct, node));
    });
    const pageCards = selected.map(item => sourceCards.get(item.id));
    if (pageCards.some(node => !node)) throw new Error('Missing product card');
    const slots = cards(grid);
    if (slots.length !== pageCards.length) throw new Error('Collection page size changed');
    slots.forEach((slot, index) => {
      const replacement = pageCards[index].cloneNode(true);
      if (index < 2) replacement.querySelectorAll('img[loading="lazy"]').forEach(img => img.setAttribute('loading', 'eager'));
      slot.replaceWith(replacement);
    });
    grid.dataset.stockOrdered = 'true';
    return doc.body.innerHTML;
  }

  function replaceCards(grid, replacements) {
    cards(grid).forEach((slot, index) => slot.replaceWith(replacements[index]));
  }

  async function mount() {
    const boot = window.CollectionStockBoot;
    const grid = document.querySelector(selector);
    const container = document.getElementById('ProductGridContainer');
    if (!grid || !container || manual(new URL(location.href)) || boot?.expired) {
      boot?.finish();
      return;
    }
    const startURL = location.href;
    const signal = initialController.signal;
    container.setAttribute('aria-busy', 'true');
    try {
      const info = metadata(grid);
      const pool = await getPool(startURL, document, signal);
      const selected = pool.ordered.slice((info.page - 1) * info.size, info.page * info.size);
      const slots = cards(grid);
      if (slots.every((node, index) => node.dataset.stockProduct === selected[index]?.id)) {
        grid.dataset.stockOrdered = 'true';
        return;
      }
      const sourceCards = new Map();
      await parallel([...new Set(selected.map(item => item.page))], async page => {
        (await pool.source(page, signal)).forEach(node => sourceCards.set(node.dataset.stockProduct, node));
      });
      if (signal.aborted || boot?.expired || location.href !== startURL || document.querySelector(selector) !== grid) return;
      if (slots.length !== selected.length) throw new Error('Collection page size changed');
      const replacements = selected.map((item, index) => {
        const source = sourceCards.get(item.id);
        if (!source) throw new Error('Missing product card');
        const replacement = source.cloneNode(true);
        if (index < 2) replacement.querySelectorAll('img[loading="lazy"]').forEach(img => {
          img.setAttribute('loading', 'eager');
          img.setAttribute('fetchpriority', 'high');
        });
        return replacement;
      });
      replaceCards(grid, replacements);
      grid.dataset.stockOrdered = 'true';
    } catch (error) {
      if (!signal.aborted) console.error('Collection stock ordering was not initialized', error);
    } finally {
      container.removeAttribute('aria-busy');
      boot?.finish();
    }
  }

  let resolveInitial;
  initial = new Promise(resolve => { resolveInitial = resolve; });
  const start = () => {
    if (started || !document.querySelector('[data-stock-mount]')) return;
    started = true;
    observer.disconnect();
    mount().finally(resolveInitial);
  };
  window.CollectionStockOrder = {
    prepare,
    ready: () => initial,
    clear: () => pools.clear(),
    cancelInitial: () => initialController.abort()
  };
  const observer = new MutationObserver(start);
  observer.observe(document.documentElement, { childList: true, subtree: true });
  start();
  document.addEventListener('DOMContentLoaded', () => {
    start();
    observer.disconnect();
    if (!started) {
      window.CollectionStockBoot?.finish();
      resolveInitial();
    }
  }, { once: true });
})();
